// Phased install UI (migration 0040; invoice generation removed in migration
// 0052). Insulation always; gutter once phased (migration 0061 full parity).
//
// Components exported from this file (single module for compactness; can be
// split per-component later if any grows substantially):
//
//   PhasesTimelineCard    — list of phases with status pills + action buttons,
//                           the computed per-phase amount, and an editable
//                           "QBO Invoice #" field (migration 0052 — the app no
//                           longer generates invoices; the office invoices in
//                           QuickBooks and records the number here)
//   RemainingPoolCard     — what's still unscheduled per BoM line
//   SchedulePhaseDialog   — create a phase (date, crew, items + qtys)
//
// All mounted from JobDetailPage.tsx — for insulation jobs, and for gutter jobs
// once they're phased / install-schedulable (migration 0061).

import { Fragment, useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Calendar, Trash2, Plus, Check, AlertCircle, ChevronDown, RotateCcw, Pencil } from 'lucide-react';
import { isPostInstallStatus, isInstallSchedulable } from '../../lib/calendar-dates.js';
import { isBlockingError } from '../../lib/isBlockingError.js';
import { Card } from '../ui/card.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Input } from '../ui/input.js';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog.js';
import {
  getJobPhases,
  postPhase,
  deletePhase,
  postPhaseComplete,
  postPhaseUncomplete,
  patchPhase,
  setPhaseInvoiceNumber,
  type Phase,
  type PoolItem,
  type CreatePhaseInput,
} from '../../api/client.js';
import { formatCents, todayIso } from '../../lib/format.js';
import { PeopleMultiSelect } from '../PeopleMultiSelect.js';

function newIdempotencyKey(): string {
  // crypto.randomUUID() is only available in secure contexts (HTTPS or
  // localhost). DEV is often hit over HTTP from a LAN IP, where it's
  // undefined. Fall back to a hand-rolled UUID v4 using getRandomValues
  // (or Math.random as last resort).
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  // RFC 4122 §4.4: version = 4, variant = 10
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function formatPhaseStatus(p: Phase): { label: string; tone: 'gray' | 'blue' | 'green' } {
  // Pull-removal (2026-06-09): the intermediate 'Pulled' state is gone.
  if (p.completedAt) return { label: 'Complete', tone: 'green' };
  return { label: 'Scheduled', tone: 'gray' };
}

// ============================================================
// PhasesTimelineCard
// ============================================================

export function PhasesTimelineCard({
  jobId,
  jobStatus,
  embedded = false,
}: {
  jobId: string;
  /** Phase 1.4 Item 16 (SEV2-1): drives read-only gating. When the job is
   *  post-install or disqualified, the Actions cell + "Schedule a phase"
   *  button are hidden — chevron-expand still works for inspection. */
  jobStatus: string;
  /** When true, render as a divided sub-section (no own Card) — folded into the
   *  Schedule card below the Estimate sub-section. Mirrors WarrantyVisitsCard. */
  embedded?: boolean;
}) {
  const qc = useQueryClient();
  const readOnly = isPostInstallStatus(jobStatus) || jobStatus === 'disqualified';
  // `readOnly` (post-install || disqualified) gates the "still building"
  // actions: Schedule a phase / Mark complete / Edit / Delete. But the QBO
  // invoice-number field and Uncomplete are POST-INSTALL actions — completing
  // the last phase flips the job to 'installed', and that's exactly when the
  // office invoices in QuickBooks. So those gate only on `isDisqualified`,
  // not on `readOnly`.
  const isDisqualified = jobStatus === 'disqualified';
  const { data, isLoading, error } = useQuery({
    queryKey: ['job-phases', jobId],
    queryFn: () => getJobPhases(jobId),
  });
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [editingPhaseId, setEditingPhaseId] = useState<string | null>(null);
  // Phase 1.4 Item 16: per-row expand/collapse state. Set pattern (replace,
  // not mutate) so React picks up the change. Orphan IDs left after delete
  // are inert — `data.phases.find(p => p.id === id)` returns undefined and
  // the row simply doesn't render.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Migration 0043 / /architect pass 2 SEV2-6: every phase mutation
  // also busts the calendar query keys so the side drawer + calendar
  // day modal see the new phase pill / item count without a manual
  // refresh.
  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['job-phases', jobId] });
    qc.invalidateQueries({ queryKey: ['job', jobId] });
    qc.invalidateQueries({ queryKey: ['calendar', 'assignments'] });
    qc.invalidateQueries({ queryKey: ['calendar', 'unassigned'] });
  };

  const completeMutation = useMutation({
    mutationFn: (phaseId: string) => postPhaseComplete(jobId, phaseId, newIdempotencyKey()),
    onSettled: invalidateAll,
  });
  const uncompleteMutation = useMutation({
    mutationFn: (phaseId: string) => postPhaseUncomplete(jobId, phaseId, newIdempotencyKey()),
    onSettled: invalidateAll,
    onError: (err: unknown) => {
      // Migration 0052: uncomplete is unconditional now (no invoice to void
      // first). Surface any other failure.
      const message = err instanceof Error ? err.message : 'Uncomplete failed';
      alert(message);
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (phaseId: string) => deletePhase(jobId, phaseId),
    onSettled: invalidateAll,
  });

  if (isLoading) return <Card className="p-4 text-sm text-gray-500">Loading phases…</Card>;
  if (isBlockingError(error, !!data)) return <Card className="p-4 text-sm text-red-600">Failed to load phases</Card>;
  if (!data) return null;

  // Phase 1.4 Item 16: client-side join from item.fieldEstimateId to
  // {description, unit}. remainingPool already covers every quote-context
  // field_estimate on the job (no filter on available > 0), so every
  // phase.items[i].fieldEstimateId should resolve. The '(removed)'
  // fallback per /architect SEV2-2 is defensive — currently unreachable
  // since field_estimates aren't deleted in prod paths.
  const itemLookup = new Map<string, { description: string; unit: string }>();
  for (const r of data.remainingPool) {
    itemLookup.set(r.fieldEstimateId, { description: r.description, unit: r.unit });
  }

  const body = (
    <>
      <div className="flex items-center justify-between mb-3">
        <h3 className="eyebrow flex items-center gap-1.5">
          <Calendar className="h-3 w-3" /> Install Phases
        </h3>
        {/* Phase 1.4 Item 16 SEV2-1: hide "Schedule a phase" when the job is
            post-install or disqualified. Plus: install crews can't be
            dispatched before the job is accepted, so the create button only
            shows at accepted/scheduled; pre-accepted shows a hint instead.
            (readOnly still governs the per-phase action cells below.) */}
        {isInstallSchedulable(jobStatus) ? (
          <Button size="sm" onClick={() => setScheduleOpen(true)}>
            <Plus size={14} className="mr-1" /> Schedule a phase
          </Button>
        ) : !readOnly ? (
          <span className="text-xs text-muted-foreground">
            Available once the job is accepted.
          </span>
        ) : null}
      </div>
      {data.phases.length === 0 ? (
        <p className="text-sm text-gray-500">No phases scheduled yet.</p>
      ) : (
        <table className="w-full text-sm table-fixed [&_td]:align-middle">
          <colgroup>
            <col style={{ width: '9%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '17%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '12%' }} />
          </colgroup>
          <thead className="text-xs text-gray-500">
            <tr>
              <th className="text-left py-1">Phase</th>
              <th className="text-left py-1">Date</th>
              <th className="text-left py-1">People</th>
              <th className="text-left py-1">Items</th>
              <th className="text-right py-1">Amount</th>
              <th className="text-left py-1">Status</th>
              <th className="text-left py-1">QBO #</th>
              <th className="text-right py-1">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.phases.map((p) => {
              const s = formatPhaseStatus(p);
              const canDelete = !p.completedAt;
              const isExpanded = expanded.has(p.id);
              // A phase scheduled for a future date can't be marked installed
              // yet (client-local today). The worker enforces the same.
              const isFuture = p.assignedDateStart > todayIso();
              return (
                <Fragment key={p.id}>
                <tr className="border-t">
                  <td className="py-2">
                    <button
                      type="button"
                      onClick={() => toggleExpand(p.id)}
                      className="mr-1 rounded p-0.5 text-gray-500 hover:text-gray-800 transition-colors align-middle"
                      title={isExpanded ? 'Collapse items' : 'Expand to see items + qty'}
                      aria-expanded={isExpanded}
                    >
                      <ChevronDown
                        size={14}
                        className={isExpanded ? '' : '-rotate-90'}
                      />
                    </button>
                    #{p.phaseNumber}
                  </td>
                  <td className="py-2 whitespace-nowrap">
                    {p.assignedDateStart === p.assignedDateEnd
                      ? p.assignedDateStart
                      : `${p.assignedDateStart} → ${p.assignedDateEnd}`}
                  </td>
                  <td className="py-2">
                    {/* truncate lives on an inner block — text-overflow:ellipsis
                        is unreliable applied directly to a table-cell. */}
                    <div
                      className="truncate"
                      title={
                        p.people.length > 0
                          ? p.people.map((pp) => pp.name).join(', ')
                          : p.crewName
                      }
                    >
                      {p.people.length > 0
                        ? p.people.slice(0, 2).map((pp) => pp.name).join(', ') +
                          (p.people.length > 2 ? ` +${p.people.length - 2}` : '')
                        : p.crewName}
                    </div>
                  </td>
                  <td className="py-2">{p.items.length}</td>
                  <td className="py-2 text-right tabular-nums whitespace-nowrap">{formatCents(p.amountCents)}</td>
                  <td className="py-2 whitespace-nowrap">
                    <Badge
                      className={
                        s.tone === 'green'
                          ? 'bg-success/10 text-success'
                          : s.tone === 'blue'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-700'
                      }
                    >
                      {s.label}
                    </Badge>
                  </td>
                  <td className="py-2">
                    <PhaseInvoiceNumberField
                      jobId={jobId}
                      phaseId={p.id}
                      value={p.externalInvoiceNumber}
                      disabled={isDisqualified || !p.completedAt}
                      disabledTitle={
                        !p.completedAt && !isDisqualified
                          ? 'Available once the phase is installed'
                          : undefined
                      }
                      onSaved={invalidateAll}
                    />
                  </td>
                  <td className="py-2 text-right">
                    {/* Phase 1.4 SEV2-1: hide action buttons for post-install
                        / disqualified jobs. Cell remains for layout
                        consistency (column count stable for colSpan math).
                        Complete is a green check toggle (outline→fill); once
                        complete, a red revert arrow appears (mirrors the Tasks
                        modal). Keeps the Actions column compact + uncluttered. */}
                    <div className="flex items-center justify-end gap-0.5">
                      {!readOnly && !p.completedAt && (
                        <button
                          type="button"
                          onClick={() => { if (!isFuture) completeMutation.mutate(p.id); }}
                          disabled={completeMutation.isPending || isFuture}
                          title={isFuture ? 'Available on or after the scheduled date' : 'Mark complete'}
                          aria-label="Mark phase complete"
                          className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-success bg-white text-success transition-colors hover:bg-success/10 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Check size={12} strokeWidth={3} />
                        </button>
                      )}
                      {p.completedAt && (
                        <span
                          className="flex h-5 w-5 items-center justify-center rounded-full bg-success text-success-foreground"
                          title="Completed"
                          aria-label="Phase complete"
                        >
                          <Check size={12} strokeWidth={3} />
                        </span>
                      )}
                      {!isDisqualified && p.completedAt && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                          onClick={() => {
                            if (
                              confirm(
                                `Uncomplete phase #${p.phaseNumber}? This reverts installed_at on all items and the assignment's completed_at.`,
                              )
                            ) {
                              uncompleteMutation.mutate(p.id);
                            }
                          }}
                          disabled={uncompleteMutation.isPending}
                          title="Revert this phase to scheduled"
                          aria-label="Revert phase to scheduled"
                        >
                          <RotateCcw size={14} />
                        </Button>
                      )}
                      {!readOnly && !p.completedAt && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0"
                          onClick={() => setEditingPhaseId(p.id)}
                          title="Edit phase"
                          aria-label="Edit phase"
                        >
                          <Pencil size={14} />
                        </Button>
                      )}
                      {!readOnly && canDelete && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0"
                          onClick={() => {
                            if (confirm(`Delete phase #${p.phaseNumber}?`)) {
                              deleteMutation.mutate(p.id);
                            }
                          }}
                          title="Delete phase"
                          aria-label="Delete phase"
                        >
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="bg-gray-50">
                    <td colSpan={8} className="px-4 py-2">
                      {p.items.length === 0 ? (
                        <p className="text-xs text-gray-500 italic">No items in this phase.</p>
                      ) : (
                        <ul className="space-y-0.5 text-xs text-gray-700">
                          {p.items.map((it) => {
                            const meta = itemLookup.get(it.fieldEstimateId)
                              ?? { description: '(removed)', unit: '' };
                            return (
                              <li
                                key={it.id}
                                className="truncate"
                                title={`${meta.description} × ${String(it.plannedQuantity)} ${meta.unit}`}
                              >
                                {meta.description} × {String(it.plannedQuantity)} {meta.unit}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
      <SchedulePhaseDialog
        jobId={jobId}
        open={scheduleOpen || editingPhaseId !== null}
        onOpenChange={(o) => {
          if (!o) {
            setScheduleOpen(false);
            setEditingPhaseId(null);
          } else {
            setScheduleOpen(true);
          }
        }}
        pool={data.remainingPool}
        editingPhase={data.phases.find((p) => p.id === editingPhaseId) ?? null}
      />
    </>
  );

  return embedded ? (
    <div className="mt-4 border-t border-border pt-4">{body}</div>
  ) : (
    <Card className="p-4">{body}</Card>
  );
}

// ============================================================
// PhaseInvoiceNumberField — inline editable QBO invoice number for a phase.
// Persists on blur (or Enter) via setPhaseInvoiceNumber; clearing the field
// and blurring sends null. Migration 0052: the office records the QuickBooks
// invoice number here; the app no longer generates invoices.
// ============================================================

function PhaseInvoiceNumberField({
  jobId,
  phaseId,
  value,
  disabled,
  disabledTitle,
  onSaved,
}: {
  jobId: string;
  phaseId: string;
  value: string | null;
  disabled?: boolean;
  /** Tooltip shown when the field is disabled (e.g. "Available once the phase
   *  is installed"). Helps the office discover WHY they can't type yet. */
  disabledTitle?: string;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState(value ?? '');
  // Re-sync the local draft when the server value changes (e.g. after a
  // refetch following save, or a revert that cleared it).
  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  const mutation = useMutation({
    mutationFn: (next: string | null) => setPhaseInvoiceNumber(jobId, phaseId, next),
    onSettled: onSaved,
    onError: (err) => {
      setDraft(value ?? '');
      alert(err instanceof Error ? err.message : 'Failed to save invoice number');
    },
  });

  const commit = () => {
    const trimmed = draft.trim();
    const next = trimmed === '' ? null : trimmed;
    if (next === (value ?? null)) return; // no change
    mutation.mutate(next);
  };

  return (
    <Input
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
      disabled={disabled || mutation.isPending}
      title={disabled ? disabledTitle : undefined}
      placeholder="QBO #"
      className="h-8 w-full text-sm"
    />
  );
}

// ============================================================
// RemainingPoolCard
// ============================================================

export function RemainingPoolCard({
  jobId,
  embedded = false,
}: {
  jobId: string;
  /** Folded into the Schedule card as a divided sub-section. */
  embedded?: boolean;
}) {
  const { data } = useQuery({
    queryKey: ['job-phases', jobId],
    queryFn: () => getJobPhases(jobId),
  });
  if (!data) return null;
  const totalRemaining = data.remainingPool.reduce((s, r) => s + r.available, 0);
  const body = (
    <>
      <h3 className="eyebrow mb-3">Remaining Pool</h3>
      {totalRemaining === 0 ? (
        <p className="text-sm text-gray-500">Nothing remaining — every BoM item is either scheduled or installed.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs text-gray-500">
            <tr>
              <th className="text-left py-1">Item</th>
              <th className="text-right py-1">Total</th>
              <th className="text-right py-1">Installed</th>
              <th className="text-right py-1">Scheduled</th>
              <th className="text-right py-1">Available</th>
            </tr>
          </thead>
          <tbody>
            {data.remainingPool.map((r) => (
              <tr key={r.fieldEstimateId} className="border-t">
                <td className="py-2">{r.description}</td>
                <td className="py-2 text-right">{r.totalQuantity} {r.unit}</td>
                <td className="py-2 text-right">{r.installedQuantity}</td>
                <td className="py-2 text-right">{r.plannedPending}</td>
                <td className="py-2 text-right font-semibold">{r.available}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );

  return embedded ? (
    <div className="mt-4 border-t border-border pt-4">{body}</div>
  ) : (
    <Card className="p-4">{body}</Card>
  );
}

// ============================================================
// SchedulePhaseDialog
// ============================================================

function SchedulePhaseDialog({
  jobId,
  open,
  onOpenChange,
  pool,
  editingPhase,
}: {
  jobId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  pool: PoolItem[];
  /** When non-null, the dialog renders in Edit mode: state pre-filled
   *  from the existing phase, submit verb is "Update", call goes to
   *  PATCH instead of POST. Pull-removal (2026-06-09): crew + items + dates
   *  are all editable until the phase completes (no dates-only lock). */
  editingPhase?: Phase | null;
}) {
  const qc = useQueryClient();
  const isEditing = !!editingPhase;

  const [personIds, setPersonIds] = useState<string[]>([]);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [items, setItems] = useState<Record<string, number>>({});

  // Pre-fill state when entering Edit mode. Effect runs when the phase
  // identity changes (entering Edit) or when the dialog reopens.
  useEffect(() => {
    if (!open) return;
    if (editingPhase) {
      setPersonIds(editingPhase.people.map((p) => p.id));
      setDate(editingPhase.assignedDateStart);
      setEndDate(editingPhase.assignedDateEnd);
      const m: Record<string, number> = {};
      for (const i of editingPhase.items) m[i.fieldEstimateId] = Number(i.plannedQuantity);
      setItems(m);
    } else {
      setPersonIds([]);
      const today = new Date().toISOString().slice(0, 10);
      setDate(today);
      setEndDate(today);
      setItems({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingPhase?.id]);

  // Idempotency-Key regenerates on each dialog open (cancel + reopen with
  // different intent gets a fresh key) AND on successful close (next open
  // already has the new key staged). Do NOT regen on error — retrying a
  // failed submit with the same body should hit the server's dedupe, not
  // write twice. Phase 1.1 Item 3.
  const [idemKey, setIdemKey] = useState(newIdempotencyKey);
  useEffect(() => {
    if (open) setIdemKey(newIdempotencyKey());
  }, [open]);
  const [error, setError] = useState<string | null>(null);

  // For Edit mode, include the editing phase's items in the pool so the
  // user can see what they're currently committed to. The server's
  // validateOverPlan helper excludes this phase from the "in-flight"
  // sum, so the office can resize/remove the existing items freely.
  const availablePool = useMemo(() => {
    if (!isEditing) return pool.filter((p) => p.available > 0);
    const editingItems = new Set(editingPhase!.items.map((i) => i.fieldEstimateId));
    return pool.filter((p) => p.available > 0 || editingItems.has(p.fieldEstimateId));
  }, [pool, isEditing, editingPhase]);

  const mutation = useMutation({
    mutationFn: (input: CreatePhaseInput) =>
      isEditing
        ? patchPhase(
            jobId,
            editingPhase!.id,
            {
              personIds: input.personIds,
              assignedDateStart: input.assignedDateStart,
              assignedDateEnd: input.assignedDateEnd,
              items: input.items,
            },
            idemKey,
          ).then(() => ({ phaseId: editingPhase!.id, phaseNumber: editingPhase!.phaseNumber }))
        : postPhase(jobId, input, idemKey),
    onSuccess: () => {
      // Migration 0043 cache invalidation — bust calendar too so side
      // drawer + day modal show the new phase pill.
      qc.invalidateQueries({ queryKey: ['job-phases', jobId] });
      qc.invalidateQueries({ queryKey: ['job', jobId] });
      qc.invalidateQueries({ queryKey: ['job-invoices', jobId] });
      qc.invalidateQueries({ queryKey: ['calendar', 'assignments'] });
      qc.invalidateQueries({ queryKey: ['calendar', 'unassigned'] });
      setIdemKey(newIdempotencyKey());
      onOpenChange(false);
    },
    onError: (err) => setError(err instanceof Error ? err.message : String(err)),
  });

  function submit() {
    setError(null);
    if (personIds.length === 0) {
      setError('Pick at least one person');
      return;
    }
    const itemList = Object.entries(items)
      .filter(([, qty]) => qty > 0)
      .map(([fieldEstimateId, plannedQuantity]) => ({ fieldEstimateId, plannedQuantity }));
    if (itemList.length === 0) {
      setError('Add at least one item with a quantity');
      return;
    }
    mutation.mutate({
      personIds,
      assignedDateStart: date,
      assignedDateEnd: endDate,
      items: itemList,
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? `Edit phase #${editingPhase!.phaseNumber}` : 'Schedule a phase'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-500">People</label>
            <PeopleMultiSelect
              value={personIds}
              onChange={setPersonIds}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500">Start</label>
              <Input
                type="date"
                value={date}
                onChange={(e) => {
                  const v = e.target.value;
                  setDate(v);
                  // Snap end forward — mirrors JobScheduleSection:374-378
                  // so a future start date can't leave end stranded in
                  // the past.
                  if (endDate && v > endDate) setEndDate(v);
                }}
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">End</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={date}
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-500 mb-1 block">Items in this phase</label>
            {availablePool.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No items available — pool is empty.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs text-gray-500">
                  <tr>
                    <th className="text-left py-1">Item</th>
                    <th className="text-right py-1">Available</th>
                    <th className="text-right py-1 w-32">This phase</th>
                  </tr>
                </thead>
                <tbody>
                  {availablePool.map((p) => (
                    <tr key={p.fieldEstimateId} className="border-t">
                      <td className="py-2">{p.description}</td>
                      <td className="py-2 text-right">{p.available} {p.unit}</td>
                      <td className="py-2 text-right">
                        <Input
                          type="number"
                          min={0}
                          max={p.available}
                          value={items[p.fieldEstimateId] ?? ''}
                          onChange={(e) => {
                            const v = Number(e.target.value);
                            setItems((prev) => ({ ...prev, [p.fieldEstimateId]: v }));
                          }}
                          className="text-right disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {error && (
            <p className="text-sm text-red-600 flex items-center gap-1">
              <AlertCircle size={14} /> {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={mutation.isPending}>
            {mutation.isPending
              ? isEditing
                ? 'Updating…'
                : 'Scheduling…'
              : isEditing
                ? 'Update phase'
                : 'Schedule phase'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

